import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";

import {
  LayoutDashboard,
  Folder,
  Kanban,
  ListTodo,
  Bug,
  Clock,
  BarChart3,
  Users,
  Settings,
  Plus,
} from "lucide-react";
const isAdmin = (role?: string) => role === 'admin' || role === 'hr' || role === 'product_manager' || role === 'scrum_master';

/* ================= HELPERS ================= */
const setHash = (hash: string) => {
  window.location.hash = hash;
};

type Project = {
  id: number;
  name: string;
  project_key: string;
  description: string;
  assigned_user_name?: string;
};
type CreateProjectModalProps = {
  onClose: () => void;
  refreshProjects: () => void;
  user: any;
};

/* ================= MAIN COMPONENT ================= */

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default function Projects() {
  const { user } = useAuth();

  const [showCreateProject, setShowCreateProject] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);

  const [clients, setClients] = useState<any[]>([]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/get_projects.php`
      );
      const data = await res.json();
      setProjects(data);
    } catch (err) {
      console.log("Error loading projects", err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/users_list.php`
      );
      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        setClients(data.data);
      }
    } catch (err) {
      console.log("Error loading users", err);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchAllUsers();
  }, []);

  return (
    <div className="flex flex-col flex-1 bg-[#f6f7fb]">

      {/* ================= BODY ================= */}
      <div className="flex flex-1 overflow-hidden">

        {/* ================= SIDEBAR ================= */}
        <aside className="w-64 shrink-0 bg-white border-r px-4 py-5 overflow-y-auto">
          {isAdmin(user?.role) && (
            <button className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-2 rounded-md font-medium mb-6">
              <Plus size={16} /> Create Issue
            </button>
          )}

          <nav className="space-y-1 text-sm">
            <SidebarItem icon={<LayoutDashboard size={16} />} label="Main Dashboard" onClick={() => setHash("dashboard")} />

            {isAdmin(user?.role) && (
              <>
                <SidebarItem icon={<LayoutDashboard size={16} />} label="Jira Dashboard" onClick={() => setHash("jira")} />
                <SidebarItem icon={<Folder size={16} />} label="Projects" active onClick={() => setHash("projects")} />
              </>
            )}

            <SidebarItem icon={<Kanban size={16} />} label="Board" onClick={() => setHash("board")} />

            {isAdmin(user?.role) && (
              <>
                <SidebarItem icon={<ListTodo size={16} />} label="Backlog" onClick={() => setHash("backlog")} />
                <SidebarItem icon={<Bug size={16} />} label="Issues" onClick={() => setHash("issues")} />
                <SidebarItem icon={<Clock size={16} />} label="Time Tracking" onClick={() => setHash("time")} />
                <SidebarItem icon={<BarChart3 size={16} />} label="Reports" onClick={() => setHash("reports")} />
                <SidebarItem icon={<Users size={16} />} label="Team" onClick={() => setHash("team")} />
                <SidebarItem icon={<Settings size={16} />} label="Settings" onClick={() => setHash("settings")} />
              </>
            )}
          </nav>
        </aside>

        {/* ================= MAIN ================= */}
        <main className="flex-1 p-6 overflow-y-auto">

          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-semibold">Projects</h1>
              <p className="text-sm text-gray-500">
                Manage your projects and teams
              </p>
            </div>

            <button
              onClick={() => setShowCreateProject(true)}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              <Plus size={16} /> New Project
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.length > 0 ? (
              projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  id={project.id}
                  tag={project.project_key}
                  title={project.name}
                  desc={project.description}
                  assignedUser={project.assigned_user_name}
                  user={user}
                  onAssign={fetchProjects}
                  clients={clients}
                />
              ))
            ) : (
              <p className="text-gray-500">No projects found</p>
            )}
          </div>

        </main>
      </div>


      {showCreateProject && (
        <CreateProjectModal
          onClose={() => setShowCreateProject(false)}
          refreshProjects={fetchProjects}
          user={user}
        />
      )}
    </div>
  );
}

/* ================= SIDEBAR ITEM ================= */

function SidebarItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition
      ${active ? "bg-green-100 text-green-700 font-medium" : "hover:bg-gray-100 text-gray-700"}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/* ================= PROJECT CARD ================= */
function ProjectCard({
  id,
  tag,
  title,
  desc,
  assignedUser,
  user,
  onAssign,
  clients,
}: {
  id: number;
  tag: string;
  title: string;
  desc: string;
  assignedUser?: string;
  user: any;
  onAssign: () => void;
  clients: any[];
}) {
  const [selectedName, setSelectedName] = useState("");

  const handleAssign = async () => {
    // Find user ID by name (trim and case-insensitive)
    const client = clients.find(
      (c) => c.name.trim().toLowerCase() === selectedName.trim().toLowerCase()
    );

    if (!client) {
      alert("Please enter or select a valid person from the list! ⚠️");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/assign_specific_user.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: id,
            assigned_to: client.id,
            assigned_by: user?.id || 1,
          }),
        }
      );

      const data = await res.json();

      if (data.status === "success") {
        alert(`Project ${data.project_name} assigned to ${data.user_name} ✅`);
        setSelectedName(""); // Clear input after successful assignment
        onAssign(); // Refresh projects list to show the assignment
      } else {
        alert(`Assignment Failed: ${data.message || "Unknown error"} ❌`);
      }
    } catch (err) {
      console.error("Assignment error:", err);
      alert("Network error or server failed ❌");
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
          {tag}
        </span>
        <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">
          active
        </span>
      </div>

      <h3 className="font-semibold mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-2 truncate" title={desc}>
        {desc}
      </p>

      {assignedUser && (
        <div className="mb-3">
          <p className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">
            Assigned to
          </p>
          <div className="flex flex-wrap gap-1.5">
            {assignedUser.split(', ').map((name, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded border border-blue-100"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 🔥 ASSIGN BY NAME */}
      <div className="mb-3">
        <label className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">
          Type Name to Assign
        </label>
        <input
          list={`names-${id}`}
          value={selectedName}
          onChange={(e) => setSelectedName(e.target.value)}
          placeholder="Type person's name..."
          className="w-full text-xs border rounded px-2 py-1.5 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-green-500"
        />
        <datalist id={`names-${id}`}>
          {clients.map((c: any) => (
            <option key={c.id} value={c.name} />
          ))}
        </datalist>
      </div>

      <button
        onClick={handleAssign}
        disabled={!selectedName.trim()}
        className={`px-3 py-1.5 rounded text-xs w-full font-medium transition
          ${selectedName.trim()
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
      >
        Assign Project
      </button>
    </div>
  );
}


/* ================= MODAL ================= */
function CreateProjectModal({
  onClose,
  refreshProjects,
  user,
}: CreateProjectModalProps) {
  const [name, setName] = useState("");
  const [projectKey, setProjectKey] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);


  const handleCreate = async () => {
    console.log("Sending data:", {
      name,
      project_key: projectKey,
      description,
      user_id: user?.id || 1,
    });

    try {
      const res = await fetch(
        `${API_BASE}/add_project.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            project_key: projectKey,
            description,
            user_id: user?.id || 1,
          }),
        }
      );

      const data = await res.json();
      console.log("Response:", data);

      if (data.status === "success") {
        alert("Project created successfully ✅");
        refreshProjects();
        onClose();
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-[420px] rounded-lg shadow-lg p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>

        <h2 className="text-lg font-semibold mb-4">Create New Project</h2>

        <div className="space-y-4">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter project name"
            className="w-full border rounded-md px-3 py-2 text-sm"
          />

          <input
            value={projectKey}
            onChange={(e) => setProjectKey(e.target.value)}
            placeholder="e.g. WEB, MOB, CRM"
            className="w-full border rounded-md px-3 py-2 text-sm"
          />

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Project description"
            className="w-full border rounded-md px-3 py-2 text-sm"
          />

          <button
            onClick={handleCreate}
            className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-md text-sm font-medium"
          >
            Create Project
          </button>
        </div>
      </div>
    </div>
  );
}
